// Pure helpers for the catalog board's drag-and-drop. Kept out of the component
// so the move/persist logic is easy to follow and unit-friendly. The board
// models its state as an ordered list of category ids plus a map of category id
// → ordered product array; dnd-kit ids are the strings `category-<id>` and
// `product-<id>`.

export const productDndId = (id) => `product-${id}`;
export const categoryDndId = (id) => `category-${id}`;

const parseId = (dndId) => Number.parseInt(String(dndId).split('-')[1], 10);

// Build the board model from the flat redux lists, preserving server order.
export const groupBoard = (categories, products) => {
  const byOrder = (a, b) => (a.display_order - b.display_order) || a.name.localeCompare(b.name);
  const order = [...categories].sort(byOrder).map((c) => c.id);
  const items = {};
  order.forEach((catId) => { items[catId] = []; });
  [...products].sort(byOrder).forEach((p) => {
    if (items[p.category_id]) items[p.category_id].push(p);
  });
  return { order, items };
};

// Which category (container) a dnd id currently belongs to, or null. A
// `category-*` id (the sortable column) and a `dropzone-*` id (the inner
// products drop area inside that column) both resolve to the same container;
// a `product-*` id resolves to whichever list holds it.
export const findContainer = (items, dndId) => {
  const s = String(dndId);
  if (s.startsWith('category-')) return parseId(dndId);
  if (s.startsWith('dropzone-')) return parseId(dndId);
  const productId = parseId(dndId);
  return Object.keys(items).find((catId) => items[catId].some((p) => p.id === productId)) ?? null;
};

// Reorder payload (for /api/admin/catalog/reorder and the optimistic reducer):
// every product in the affected categories gets its new 0-based display_order
// and category_id. `affected` is an array of category ids (numbers or strings).
// Optional `categoryOrder` (array of category ids in their new order) emits
// matching category patches so a category-level drag persists too.
export const reorderPayload = (items, affected, categoryOrder = null) => {
  const products = [];
  affected.forEach((catId) => {
    const list = items[catId] || [];
    list.forEach((product, index) => {
      products.push({ id: product.id, display_order: index, category_id: Number(catId) });
    });
  });
  const categories = categoryOrder
    ? categoryOrder.map((id, index) => ({ id: Number(id), display_order: index }))
    : [];
  return { categories, products };
};
